import type { CityConfig } from "@/pages/city-template/types";

export const woonsocketConfig: CityConfig = {
  city: "Woonsocket",
  state: "Rhode Island",
  stateAbbr: "RI",
  slug: "woonsocket",
  route: "/cities/woonsocket",
  title: "Emergency & Expedited Passport Woonsocket, RI | Same Day Passport Near MA Border",
  metaDescription:
    "Emergency passport Woonsocket RI — same day, 24-hour, and 3-5 day expedited passport processing. Fast Passport Center serves Woonsocket, North Smithfield, Burrillville, Cumberland, and northern Rhode Island near the Massachusetts border. Skip the 8-11 week wait.",
  h1: "Emergency & Expedited Passport Services in Woonsocket, RI",
  heroSubtitle:
    "Woonsocket is northern Rhode Island's largest city — and travelers here need speed that matches their proximity to Boston and international hubs. Fast Passport Center delivers emergency passports as fast as 24 hours, with 3-5 day expedited processing for Woonsocket and all communities near the Massachusetts border.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20Woonsocket%20Rhode%20Island%20downtown%20with%20historic%20mill%20architecture%20and%20the%20Blackstone%20River%2C%20warm%20autumn%20afternoon%20light%2C%20New%20England%20industrial%20heritage%20charm%2C%20clean%20modern%20travel%20photography%20composition%2C%20no%20text%20overlay&width=1600&height=700&seq=ri-woon-hero-001&orientation=landscape",
  heroImageAlt: "Historic downtown Woonsocket Rhode Island with Blackstone River and mill architecture",
  geoPosition: "42.0029; -71.5148",
  geoPlacename: "Woonsocket, Rhode Island, United States",
  lastModified: "2026-05-31",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Thomas P. O'Neill Jr. Federal Building, 10 Causeway Street, Boston, MA 02222",
    distance: "~40 miles from Woonsocket",
  },
  nearbyCitiesZips: [
    { name: "Woonsocket", zips: "02895" },
    { name: "Cumberland", zips: "02864, 02827" },
    { name: "North Smithfield", zips: "02896, 02857, 02876" },
    { name: "Burrillville", zips: "02830, 02859, 02839" },
    { name: "Lincoln", zips: "02865, 02838" },
    { name: "Smithfield", zips: "02917" },
    { name: "Pawtucket", zips: "02860-02862" },
    { name: "Central Falls", zips: "02863" },
    { name: "Providence", zips: "02901-02912, 02918, 02940" },
    { name: "North Providence", zips: "02904, 02911" },
    { name: "Johnston", zips: "02919" },
    { name: "Cranston", zips: "02905-02907, 02910, 02920-02921" },
    { name: "Warwick", zips: "02886-02889, 02818" },
    { name: "East Providence", zips: "02914-02916" },
    { name: "East Greenwich", zips: "02818" },
    { name: "Bristol", zips: "02809" },
    { name: "Barrington", zips: "02806" },
    { name: "Newport", zips: "02840-02841" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency passport processing for Woonsocket travelers with urgent departures — same day service available for documented life-or-death emergencies.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Northern Rhode Island",
      desc: "Woonsocket, North Smithfield, Burrillville, Cumberland, Lincoln, and all communities near the Massachusetts border.",
    },
  ],
  testimonials: [
    {
      name: "Jennifer K.",
      location: "Woonsocket, RI",
      rating: 5,
      text: "I needed an emergency passport from Woonsocket for a business trip to Montreal. Fast Passport Center got it done in 3 days. The agent was incredibly helpful given how close we are to the border.",
      service: "Emergency Passport",
    },
    {
      name: "Thomas B.",
      location: "Cumberland, RI",
      rating: 5,
      text: "Renewed my expired passport through Fast Passport Center. Chose the 5-day option and had it back in 4 days. I appreciated not having to drive all the way to Providence.",
      service: "Passport Renewal",
    },
    {
      name: "Maria S.",
      location: "North Smithfield, RI",
      rating: 5,
      text: "First-time passport for my son. Fast Passport Center explained the two-parent rule and helped us get the consent forms right. Processed in 5 days and delivered to our North Smithfield home.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "woon-1",
      name: "Woonsocket Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "1270 Providence Street",
      city: "Woonsocket",
      state: "RI",
      zip: "02895",
      phone: "401-769-0550",
      hours: "Mon-Fri 8:00 AM - 4:30 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Central Woonsocket",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "woon-2",
      name: "Woonsocket Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "35 South Main Street",
      city: "Woonsocket",
      state: "RI",
      zip: "02895",
      phone: "401-762-1300",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Downtown Woonsocket",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. Photo services available on-site.",
      icon: "ri-building-2-line",
    },
    {
      id: "woon-3",
      name: "Woonsocket City Clerk",
      type: "City Clerk / Document Services",
      address: "169 Main Street",
      city: "Woonsocket",
      state: "RI",
      zip: "02895",
      phone: "401-767-2200",
      hours: "Mon-Fri 8:30 AM - 4:30 PM",
      distance: "Downtown Woonsocket",
      website: "https://www.woonsocketri.org/",
      websiteLabel: "City of Woonsocket",
      notes: "Provides document and vital records services. For passport applications, visit an authorized post office or acceptance facility.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Woonsocket, RI?",
      a: "Fast Passport Center offers same-day emergency passport processing in as little as 24 hours for Woonsocket residents with documented life-or-death emergencies. For urgent travel within 2 weeks, our 3-5 day expedited service is the most popular option. Standard processing through the Woonsocket post office takes 8-11 weeks.",
    },
    {
      q: "Where is the closest passport acceptance facility in Woonsocket?",
      a: "The Woonsocket Post Office at 1270 Providence Street and the Woonsocket Main Post Office at 35 South Main Street both offer passport acceptance services by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person at these facilities. Fast Passport Center guides you through the acceptance clerk process.",
    },
    {
      q: "Can I get same-day passport photos in Woonsocket, RI?",
      a: "Yes — passport photos are available at CVS Pharmacy, Walgreens, UPS Store, and FedEx Office locations throughout Woonsocket. We also offer photo review as part of our service to ensure your 2x2 photos meet all State Department requirements before submission.",
    },
    {
      q: "How much does an expedited passport cost in Woonsocket?",
      a: "Government fees are set by the State Department and vary based on passport type (book vs card, large book option). Fast Passport Center's service fees depend on processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote based on your Woonsocket passport needs.",
    },
    {
      q: "Do I need to drive to the Boston Passport Agency from Woonsocket?",
      a: "No — if you use Fast Passport Center, you do not need to visit the Boston Passport Agency at all. As a registered courier, we handle the agency submission for you. The only reason to visit the Boston agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain. Woonsocket is actually closer to Boston than most of Rhode Island.",
    },
    {
      q: "What is the fastest way to renew a passport in Woonsocket, Rhode Island?",
      a: "The fastest way to renew a passport in Woonsocket is through Fast Passport Center's 3-5 day expedited service. If your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged, you may qualify for renewal without an acceptance clerk visit. For even faster renewal, our 24-hour emergency service is available.",
    },
    {
      q: "Can I get a child passport expedited in Woonsocket, RI?",
      a: "Yes — Fast Passport Center expedites child passports for minors under 16 in Woonsocket and all of northern Rhode Island. Both parents must appear with the child or provide notarized consent (Form DS-3053). We review all parental consent documentation before submission to avoid delays.",
    },
    {
      q: "Does Fast Passport Center have an office in Woonsocket, RI?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Woonsocket. We serve Woonsocket and all of Rhode Island through our secure overnight shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Woonsocket", address: "various CVS locations in Woonsocket", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Woonsocket", address: "various Walgreens locations in Woonsocket", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Woonsocket", address: "various UPS Store locations in Woonsocket", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Woonsocket", address: "various FedEx Office locations near downtown Woonsocket", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Woonsocket", address: "various Walmart locations in Woonsocket", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "RI DMV — Cranston",
      address: "600 New London Ave, Cranston, RI 02920",
      hours: "Mon-Fri 8:30 AM - 3:15 PM",
      note: "Main Rhode Island DMV office. Accepts walk-ins but appointments are strongly recommended.",
      featured: true,
    },
    {
      name: "RI DMV Express — Pawtucket",
      address: "1250 Elmwood Ave, Cranston, RI 02910",
      hours: "Mon-Fri 8:30 AM - 3:15 PM",
      note: "Express DMV services for license renewals and REAL ID applications.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PVD",
      name: "T.F. Green Airport",
      distance: "~25 miles from Woonsocket",
      address: "2000 Post Rd, Warwick, RI 02886",
      note: "Rhode Island's primary airport. A short drive from Woonsocket via Route 146. Serves domestic destinations and limited international connections.",
      flightUrl: "https://www.pvdairport.com/",
      flightLabel: "T.F. Green Airport",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "~40 miles from Woonsocket",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and worldwide destinations. Woonsocket is closer to Boston than most of Rhode Island.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan International",
      featured: false,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~70 miles from Woonsocket",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's major airport with domestic and some international service. Alternative option for Woonsocket-area travelers.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Woonsocket travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Woonsocket residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Woonsocket residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Woonsocket residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Woonsocket, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Woonsocket residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Woonsocket travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Woonsocket residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Woonsocket residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Woonsocket residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Woonsocket." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Woonsocket." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Woonsocket travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Woonsocket travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Woonsocket-area team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Woonsocket address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: [
        "Mail-in application",
        "No expedited handling",
        "No dedicated agent",
        "No phone or email contact for your case",
        "Updates only by physical mail letter",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: [
        "For documented life-or-death emergencies only",
        "Requires proof of emergency travel",
        "Strictly for documented life-or-death emergencies",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Providence", route: "/cities/providence" },
    { name: "Warwick", route: "/cities/warwick" },
    { name: "Cranston", route: "/cities/cranston" },
    { name: "Pawtucket", route: "/cities/pawtucket" },
    { name: "Newport", route: "/cities/newport-ri" },
    { name: "East Providence", route: "/cities/east-providence" },
    { name: "North Providence", route: "/states/rhode-island" },
    { name: "Johnston", route: "/cities/johnston" },
    { name: "Coventry", route: "/cities/coventry" },
    { name: "West Warwick", route: "/cities/west-warwick" },
  ],
  fpcOfficeNote:
    "Fast Passport Center serves Woonsocket and all of Rhode Island as a remote courier service. We do not have a physical office in Woonsocket, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Remote courier service — Woonsocket, RI area",
  serviceAreaIntro:
    "Fast Passport Center provides expedited passport services to all Woonsocket neighborhoods and surrounding northern Rhode Island communities. From Downtown to Cass Park, Social to Fairmount Heights — we serve every Woonsocket neighborhood with the same fast, reliable processing.",
  nearbyCitiesIntro:
    "We proudly serve Woonsocket and all surrounding northern Rhode Island communities. Whether you're near the Blackstone River or in a nearby suburb, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro:
    "Our Woonsocket customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro:
    "These are the most common mistakes Woonsocket travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro:
    "Woonsocket and northern Rhode Island travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro:
    "Common questions about emergency and expedited passport services in Woonsocket, RI. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/rhode-island",
  statePageName: "Rhode Island Passport Services",
  dmvWebsite: "https://dmv.ri.gov",
  dmvLabel: "Rhode Island DMV — Official Website",
};

export default woonsocketConfig;